import { ref } from 'vue'
import { useStore } from 'vuex'
import { useToast } from './useToast'

// Idempotent HTTP methods that are safe to retry on 5xx
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Unwrap the {data, meta} response envelope returned by MCS-backed endpoints.
 * Only unwraps when BOTH 'data' and 'meta' keys are present on a plain object —
 * legacy tuple responses ([status, data]) or bare arrays/objects pass through
 * unchanged so supplier endpoints continue to work without modification.
 *
 * @param {*} response - Parsed JSON response from the API
 * @returns {*} response.data if envelope shape, otherwise response unchanged
 */
export function unwrapEnvelope(response) {
    if (
        response &&
        typeof response === 'object' &&
        !Array.isArray(response) &&
        'data' in response &&
        'meta' in response
    ) {
        return response.data
    }
    return response
}

/**
 * Bearer-authenticated fetch wrapper for views that call `fetch()` directly
 * (rather than going through the useApi() composable). Attaches the current
 * Firebase user's ID token as `Authorization: Bearer <token>` plus any
 * headers supplied in options.
 *
 * Caller keeps ownership of response handling — this helper only ensures the
 * token is attached. Use useApi()'s get/post/etc. when you also want 401/403/
 * 5xx handling + envelope unwrap.
 *
 * @param {string} url     - Fully-qualified URL to fetch
 * @param {object} options - Standard fetch options (method, body, headers, …)
 * @param {object} user    - Firebase user from the store (store.state.user)
 * @returns {Promise<Response>}
 */
export async function authFetch(url, options = {}, user = null) {
    let token = null
    if (user && typeof user.getIdToken === 'function') {
        try {
            token = await user.getIdToken()
        } catch (e) {
            console.error('authFetch: failed to obtain ID token:', e)
        }
    }
    const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
    }
    return fetch(url, { ...options, headers })
}

export function useApi() {
    const store = useStore()
    const { error: showError, warning: showWarning } = useToast()
    const isLoading = ref(false)
    const error = ref(null)

    // Preserved: Bearer token attached via store.state.user.getIdToken()
    const getAuthHeaders = async () => {
        const user = store.state.user
        if (!user) return {}

        try {
            const token = await user.getIdToken()
            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        } catch (e) {
            console.error('Error getting auth token:', e)
            return {}
        }
    }

    /**
     * Core fetch wrapper. Handles:
     *   - Bearer token attachment (preserved from original)
     *   - 401 → logout + redirect to /login
     *   - 403 → warning toast, no sign-out
     *   - 5xx → error toast + single retry on idempotent methods only
     *
     * @param {string} endpoint - Path relative to baseURL
     * @param {object} options  - fetch options (method, body, headers, …)
     * @param {boolean} _isRetry - Internal flag: true when this is the retry attempt
     */
    const request = async (endpoint, options = {}, _isRetry = false) => {
        isLoading.value = true
        error.value = null

        try {
            const headers = await getAuthHeaders()
            const baseURL = store.state.apiUrl
            const method = (options.method || 'GET').toUpperCase()

            const response = await fetch(`${baseURL}${endpoint}`, {
                ...options,
                headers: {
                    ...headers,
                    ...options.headers
                }
            })

            // 401 — token expired or invalid session: sign out and redirect
            if (response.status === 401) {
                await store.dispatch('logout')
                window.location.href = '/login'
                throw new Error('Session expired. Please login again.')
            }

            // 403 — valid session but no access to this resource: warn without sign-out
            if (response.status === 403) {
                const msg = 'Not authorized for this data.'
                error.value = msg
                showWarning(msg)
                throw new Error(msg)
            }

            // 5xx — server error: retry once on idempotent methods, then surface error
            if (response.status >= 500) {
                if (!_isRetry && IDEMPOTENT_METHODS.has(method)) {
                    await new Promise(resolve => setTimeout(resolve, 500))
                    return request(endpoint, options, true)
                }
                const msg = 'Server error — please try again.'
                error.value = msg
                showError(msg)
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
            }

            return await response.json()
        } catch (e) {
            // Only set error + show toast for errors not already handled above
            // (401/403/5xx branches set error.value and toast themselves)
            if (!error.value) {
                error.value = e.message
                showError(e.message)
            }
            throw e
        } finally {
            isLoading.value = false
        }
    }

    const get = (endpoint, params = {}) => {
        const query = new URLSearchParams(params).toString()
        const url = query ? `${endpoint}?${query}` : endpoint
        return request(url)
    }

    const post = (endpoint, data) => {
        return request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        })
    }

    const put = (endpoint, data) => {
        return request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        })
    }

    const del = (endpoint) => {
        return request(endpoint, {
            method: 'DELETE'
        })
    }

    return {
        isLoading,
        error,
        get,
        post,
        put,
        del,
    }
}

